import { useEffect, useCallback, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, AppDispatch } from '@store/index'
import { InvoiceEntry } from '@dataTypes/billing.types'
import {
  fetchInvoiceEntries,
  deleteInvoiceEntry,
  setInvoicePagination,
} from '@store/actions/billing.action'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import { useDebouncedValue } from '@hooks/useDebouncedValue'
import { SortDirection } from '@constants/Common/sort-direction.constant'
import { buildInvoiceFilters } from '../filterBuilders'

const INVOICE_SEARCH_DEBOUNCE_MS = 300

interface UseInvoiceHooks {
  invoices: InvoiceEntry[]
  invoiceCount: number
  loadingInvoices: boolean
  currentPage: number
  pageSize: number
  handlePageChange: (page: number) => void
  handlePageSizeChange: (pageSize: number) => void
  handleSortChange: (sort: string) => void
  handleTableSortChange: (sort: {
    key: string
    direction: SortDirection
  }) => void
  confirmDeleteInvoice: (invoiceNo: string) => Promise<void>
}

const useInvoice = (
  searchQuery: string,
  filters: Record<string, string[]>,
  options?: { suppressTableLoading?: boolean },
): UseInvoiceHooks => {
  const dispatch = useDispatch<AppDispatch>()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const suppressTableLoadingRef = useRef(options?.suppressTableLoading)
  const isInitialListFetchRef = useRef(true)
  suppressTableLoadingRef.current = options?.suppressTableLoading
  const { showToast } = useNotificationToast()
  const { invoices, invoiceCount, loadingInvoices, invoicePagination } =
    useSelector((state: RootState) => state.billing)
  const { currentPage, pageSize, sort } = invoicePagination

  const selectedPatient = useSelector(
    (state: RootState) => state.patient.selectedPatient,
  )

  const selectedClinic = useSelector(
    (state: RootState) => state.clinic.selectedClinic,
  )

  const debouncedSearchQuery = useDebouncedValue(
    searchQuery,
    INVOICE_SEARCH_DEBOUNCE_MS,
  )

  const listQueryFilters = useMemo(
    () =>
      buildInvoiceFilters({
        search: debouncedSearchQuery,
        tabFilters: filters,
      }),
    [debouncedSearchQuery, filters],
  )

  useEffect(() => {
    dispatch(setInvoicePagination({ currentPage: 1 }))
  }, [
    dispatch,
    selectedPatient?.patient_id,
    selectedClinic?.clinic_id,
    debouncedSearchQuery,
    filters.status,
  ])

  useEffect(() => {
    const fetchInvoices = async (): Promise<void> => {
      const suppressOverlay =
        suppressTableLoadingRef.current || isInitialListFetchRef.current
      if (!suppressOverlay) {
        showLoading({
          container: '.invoice-table',
        })
      }
      try {
        await dispatch(fetchInvoiceEntries(listQueryFilters)).unwrap()
      } finally {
        isInitialListFetchRef.current = false
        hideLoading('.invoice-table')
      }
    }
    void fetchInvoices()
  }, [
    dispatch,
    showLoading,
    hideLoading,
    currentPage,
    pageSize,
    sort,
    selectedPatient?.patient_id,
    selectedClinic?.clinic_id,
    listQueryFilters,
  ])

  const handlePageChange = useCallback(
    (page: number): void => {
      dispatch(setInvoicePagination({ currentPage: page }))
    },
    [dispatch],
  )

  const handlePageSizeChange = useCallback(
    (newPageSize: number): void => {
      dispatch(setInvoicePagination({ pageSize: newPageSize, currentPage: 1 }))
    },
    [dispatch],
  )

  const handleSortChange = useCallback(
    (newSort: string): void => {
      dispatch(setInvoicePagination({ sort: newSort, currentPage: 1 }))
    },
    [dispatch],
  )

  const handleTableSortChange = useCallback(
    ({ key, direction }: { key: string; direction: SortDirection }): void => {
      if (key === 'patientName') {
        handleSortChange(`patientName:${direction}`)
      } else if (key === 'invoiceDate') {
        handleSortChange(`invoiceDate:${direction}`)
      } else if (key === 'status') {
        handleSortChange(`status:${direction}`)
      }
    },
    [handleSortChange],
  )

  const confirmDeleteInvoice = useCallback(
    async (invoiceNo: string): Promise<void> => {
      showLoading({
        container: '.invoice-table',
      })
      try {
        await dispatch(deleteInvoiceEntry(invoiceNo)).unwrap()
        await dispatch(fetchInvoiceEntries(listQueryFilters)).unwrap()
        showToast(
          createSuccessToast(
            'Invoice deleted successfully',
            'Invoice has been deleted successfully.',
          ),
        )
      } catch {
        showToast(
          createFailedToast(
            'Invoice not deleted',
            'Invoice not deleted. Please try again.',
          ),
        )
      } finally {
        hideLoading('.invoice-table')
      }
    },
    [dispatch, showLoading, hideLoading, showToast, listQueryFilters],
  )

  return {
    invoices,
    invoiceCount,
    loadingInvoices,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    handleTableSortChange,
    confirmDeleteInvoice,
  }
}

export default useInvoice
